import {Vector3} from '@babylonjs/core/Maths/math.vector';
import {
  type BabylonPointCloud,
  type RenderFullscreen,
  type RenderScreenshot,
  type SceneBox,
} from '@wandb/weave/common/util/render_babylon';

import {resolvePointColor} from '../PanelMediaBrowser/common';
import {
  type PointCloudCardRenderRequest,
  type PointCloudFullscreenRenderRequest,
  type PointCloudRenderer,
  type PointCloudRgb,
  type PointCloudScene,
} from './types';

type BabylonLib = typeof import('@wandb/weave/common/util/render_babylon');

let babylonLibPromise: Promise<BabylonLib> | undefined;

function loadBabylonLib(): Promise<BabylonLib> {
  babylonLibPromise ??= import('@wandb/weave/common/util/render_babylon');
  return babylonLibPromise;
}

const DEFAULT_VECTOR_COLOR: PointCloudRgb = [0, 255, 255];

/**
 * Only Babylon-specific conversion: shared scene → `renderJsonPoints` cloud.
 * Does not truncate; preprocess already did. Resolves category/default colors
 * here so draw only packs RGB buffers.
 */
export function sceneToBabylonCloud(
  scene: PointCloudScene,
  pointColor?: PointCloudRgb
): BabylonPointCloud {
  return {
    points: scene.points.map(point => ({
      position: point.position,
      category: point.category,
      color: pointColor ?? resolvePointColor(point),
    })),
    vectors: scene.vectors.map(vector => ({
      start: vector.start,
      end: vector.end,
      color: vector.color ?? DEFAULT_VECTOR_COLOR,
    })),
    boxes: scene.boxes.map(
      (box): SceneBox => ({
        label: box.label,
        color: box.color,
        score: box.score,
        edges: box.edges.map(([start, end]) => [
          new Vector3(...start),
          new Vector3(...end),
        ]),
      })
    ),
  };
}

/**
 * Babylon adapter for {@link PointCloudRenderer}. Truncation and filtering
 * stay in preprocess; this only converts the shared scene and draws.
 */
export const babylonPointCloudRenderer: PointCloudRenderer = {
  id: 'babylon',

  async renderCard(request: PointCloudCardRenderRequest) {
    const babylonLib = await loadBabylonLib();
    const pointCloud = sceneToBabylonCloud(request.scene, request.pointColor);
    const result = babylonLib.renderJsonPoints<RenderScreenshot>(
      pointCloud,
      {
        fullscreen: false,
        width: request.width,
        height: request.height,
      },
      request.cameraControl,
      request.backgroundColor,
      request.useRightHandedSystem
    );
    const imageUrl = await babylonLib.renderScreenshot(result);
    return {imageUrl, cleanup: result.cleanup};
  },

  async mountFullscreen(request: PointCloudFullscreenRenderRequest) {
    const babylonLib = await loadBabylonLib();
    const pointCloud = sceneToBabylonCloud(request.scene, request.pointColor);
    const result = babylonLib.renderJsonPoints<RenderFullscreen>(
      pointCloud,
      {
        domElement: request.domElement,
        fullscreen: true,
      },
      // Matches legacy PointCloud.tsx: fullscreen does not pass cameraControl.
      undefined,
      request.backgroundColor,
      request.useRightHandedSystem
    );
    await babylonLib.renderFullscreen(result);
    return {cleanup: result.cleanup};
  },
};
